import * as React from 'react'

import { MapIcon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  useHostTooltipContainer,
  useOverlayPortalContainer,
} from '~/client/src/shared/utils/hostPortalContainer'

import { ITaggedMap } from './taggedMaps'

interface IProps {
  maps: ITaggedMap[]
  // A switch mid style load is what the map-view pill's paging guard prevents.
  isDisabled: boolean
}

const linkButton = (
  label: string,
  isDisabled: boolean,
  onClick?: () => void,
) => (
  <Button
    variant="ghost"
    size="icon-sm"
    data-stack-map-link=""
    className="tw:mr-1 tw:shrink-0"
    aria-label={label}
    disabled={isDisabled}
    onClick={onClick}
  />
)

/** A stack row's way to its Location's own maps: one opens directly, several open a menu. */
export function StackMapLink({ maps, isDisabled }: IProps): React.ReactElement {
  const portalContainer = useOverlayPortalContainer()
  const tooltipContainer = useHostTooltipContainer()
  const [single] = maps
  const label =
    maps.length === 1
      ? Localization.translator.openX(single.name)
      : Localization.translator.openAMap
  const icon = <MapIcon aria-hidden="true" className="tw:size-4" />
  const tooltip = (
    <TooltipContent
      portalContainer={tooltipContainer}
      className="tw:max-w-none tw:whitespace-nowrap"
    >
      {label}
    </TooltipContent>
  )

  if (maps.length === 1) {
    return (
      <Tooltip>
        <TooltipTrigger render={linkButton(label, isDisabled, single.open)}>
          {icon}
        </TooltipTrigger>
        {tooltip}
      </Tooltip>
    )
  }

  // Menu trigger outermost: with the tooltip's trigger outside it, the tooltip never opens.
  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger
          disabled={isDisabled}
          render={<TooltipTrigger render={linkButton(label, isDisabled)} />}
        >
          {icon}
        </DropdownMenuTrigger>
        {tooltip}
      </Tooltip>
      <DropdownMenuContent
        align="end"
        container={portalContainer}
        className="tw:w-auto tw:whitespace-nowrap"
      >
        {maps.map(map => (
          <DropdownMenuItem key={map.id} onClick={map.open}>
            <MapIcon />
            {map.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
